import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Typography,
  Tag,
  Progress,
  Alert,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  BarChartOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import { intelligenceAPI } from '../../api/config';

const { Text, Title } = Typography;
const { Option } = Select;

const fmtMoney = (v) =>
  v != null ? `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0 })}` : '—';

const ChangeCell = ({ curr, prev }) => {
  if (!prev || prev === 0) return <Text type="secondary">—</Text>;
  const diff = curr - prev;
  const pct  = ((diff / prev) * 100).toFixed(1);
  const up   = diff > 0;
  return (
    <span style={{ color: up ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
      {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {' '}{Math.abs(pct)}%
      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
        ({up ? '+' : ''}{fmtMoney(diff)})
      </Text>
    </span>
  );
};

const MonthComparison = () => {
  const permissions = useSelector((s) => s.auth.permissions || []);
  const canView = hasPermission(permissions, 'purchase_order/view');

  const [months, setMonths] = useState(6);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  const { vendors } = useSelector((s) => s.vendors || {});
  const [vendorFilter, setVendorFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { months };
      if (vendorFilter) params.vendor_id = vendorFilter;
      const res = await intelligenceAPI.getMonthComparison(params);
      setData(res.data);
    } catch (e) {
      setError('Failed to load month comparison data.');
    } finally {
      setLoading(false);
    }
  }, [months, vendorFilter]);

  useEffect(() => {
    if (canView) load();
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: 60, color: '#8c8c8c' }}>
          You don't have permission to view analytics.
        </div>
      </div>
    );
  }

  const monthRows = data?.months || [];
  const curr = data?.current_month;
  const prev = data?.previous_month;
  const maxSpend = Math.max(...monthRows.map((m) => m.total_spend || 0), 1);

  const columns = [
    {
      title: 'Month',
      dataIndex: 'month_label',
      key: 'month_label',
      render: (v, _, i) => (
        <Text strong={i === 0}>{v}</Text>
      ),
    },
    {
      title: 'Orders',
      dataIndex: 'total_orders',
      key: 'orders',
      align: 'center',
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Total Spend',
      dataIndex: 'total_spend',
      key: 'spend',
      align: 'right',
      render: (v) => <Text strong style={{ color: '#1890ff' }}>{fmtMoney(v)}</Text>,
      sorter: (a, b) => a.total_spend - b.total_spend,
    },
    {
      title: 'Spend Bar',
      dataIndex: 'total_spend',
      key: 'bar',
      render: (v) => (
        <Progress
          percent={Math.round((v / maxSpend) * 100)}
          showInfo={false}
          size="small"
          strokeColor="#1890ff"
          style={{ minWidth: 80, marginBottom: 0 }}
        />
      ),
    },
    {
      title: 'vs Prev Month',
      key: 'change',
      align: 'center',
      render: (_, record, i) => {
        if (i === monthRows.length - 1) return <Text type="secondary">—</Text>;
        return <ChangeCell curr={record.total_spend} prev={monthRows[i + 1]?.total_spend} />;
      },
    },
    {
      title: 'Total Tax',
      dataIndex: 'total_tax',
      key: 'tax',
      align: 'right',
      render: (v) => <Text style={{ color: '#fa8c16', fontSize: 12 }}>{fmtMoney(v)}</Text>,
    },
    {
      title: 'Total Discount',
      dataIndex: 'total_discount',
      key: 'discount',
      align: 'right',
      render: (v) => <Text style={{ color: '#52c41a', fontSize: 12 }}>{fmtMoney(v)}</Text>,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Header ──────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><BarChartOutlined /></div>
            <div className="page-header-text">
              <h1>Month-on-Month Spend</h1>
              <p>Compare procurement spend across recent months</p>
            </div>
          </div>
        </div>

        {/* ── Summary Cards ─────────────────────────────────────── */}
        {curr && prev && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
                <Statistic
                  title={`This Month (${curr.month_label})`}
                  value={Number(curr.total_spend).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  prefix="₹"
                  valueStyle={{ color: '#1890ff', fontSize: 18 }}
                />
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                  {curr.total_orders} orders
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: '#f9f9f9' }}>
                <Statistic
                  title={`Last Month (${prev.month_label})`}
                  value={Number(prev.total_spend).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  prefix="₹"
                  valueStyle={{ color: '#595959', fontSize: 18 }}
                />
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                  {prev.total_orders} orders
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              {(() => {
                const diff = data.change_amount ?? (curr.total_spend - prev.total_spend);
                const pct  = (data.change_pct ?? 0).toFixed(1);
                const up   = diff > 0;
                return (
                  <Card
                    size="small"
                    style={{
                      background: up ? '#fff2f0' : '#f6ffed',
                      borderColor: up ? '#ffccc7' : '#b7eb8f',
                    }}
                  >
                    <Statistic
                      title="Month-on-Month Change"
                      value={`${up ? '+' : ''}${pct}%`}
                      prefix={up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      valueStyle={{ color: up ? '#ff4d4f' : '#52c41a', fontSize: 22 }}
                    />
                    <div style={{ fontSize: 12, marginTop: 4, color: up ? '#ff4d4f' : '#52c41a' }}>
                      {up ? '+' : ''}{fmtMoney(diff)}
                    </div>
                  </Card>
                );
              })()}
            </Col>
          </Row>
        )}

        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

        {/* ── Filters ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Title level={5} style={{ margin: 0 }}>Monthly Breakdown</Title>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Select value={months} onChange={setMonths} style={{ width: 120 }}>
              <Option value={3}>Last 3 months</Option>
              <Option value={6}>Last 6 months</Option>
              <Option value={12}>Last 12 months</Option>
            </Select>
            <Select
              showSearch
              placeholder="All Vendors"
              allowClear
              style={{ width: 180 }}
              value={vendorFilter || undefined}
              onChange={(v) => setVendorFilter(v || '')}
              filterOption={(i, o) => o?.children?.toLowerCase().includes(i.toLowerCase())}
            >
              {(vendors || []).map((v) => (
                <Option key={v.id} value={v.id}>{v.firm_name}</Option>
              ))}
            </Select>
            <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Apply
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={monthRows}
          rowKey="month_label"
          loading={loading}
          pagination={false}
          size="small"
          rowClassName={(_, i) => i === 0 ? 'month-row-current' : ''}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                No data for the selected period. Create purchase orders to see trends.
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default MonthComparison;
